import React from 'react';

import { Box, Card, CardContent, Stack, Tooltip, Typography } from '@mui/material';

import { lppMarginPresentation } from './lppMargin';
import type { LppMapping } from './lppMargin';
import { money } from './review';
import type { HealthProperty, ReviewWindow } from './types';

export default function HotelPerformance({
  property,
  window,
  lppMapping,
  comparisonEndDate,
}: {
  property: HealthProperty;
  lppMapping: LppMapping;
  window: ReviewWindow;
  comparisonEndDate: string;
}) {
  const metrics = property.windows?.[window];
  const amounts = [
    { label: 'LPC margin', amount: metrics?.lpc_margin_ar_aud },
    { label: 'LPP margin', amount: metrics?.lpp_margin_ar_aud },
    { label: 'LPC TTV', amount: metrics?.lpc_ttv_ar_aud },
  ];
  const movement = metrics?.margin_change_7d_aud.value;
  const lpp = lppMarginPresentation(lppMapping, metrics?.lpp_margin_ar_aud);
  return (
    <Card variant="outlined">
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
          flexWrap="wrap"
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Commercial performance
            </Typography>
            <Typography variant="caption" color="text.secondary">
              AUD, after refunds · {metrics ? `${metrics.start_date} to ${metrics.end_date}` : 'Window unavailable'}
            </Typography>
          </Box>
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 2.5 }}>
          {amounts.map(({ label, amount }) => {
            const isLpp = label === 'LPP margin';
            const value = isLpp ? lpp.value : (amount?.value ?? amount?.observed_subtotal);
            const caption = isLpp
              ? lpp.kind === 'observed'
                ? null
                : lpp.caption
              : value == null
                ? 'No observations in this period'
                : amount?.value == null
                  ? 'Observed subtotal'
                  : label === 'LPC TTV' && value < 0
                    ? 'Refunds exceed sales in this period'
                    : null;
            return (
              <Box key={label}>
                <Typography variant="body2" color="text.secondary">
                  {label}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    my: 0.5,
                    color: value == null ? 'text.secondary' : 'primary.main',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {value == null ? '—' : money(value)}
                </Typography>
                {caption && (
                  <Typography variant="caption" color="text.secondary">
                    {caption}
                  </Typography>
                )}
              </Box>
            );
          })}
          <Box>
            <Tooltip title="Seven days ending on the selected end date compared with the preceding seven days. Both full periods must have usable retained observations.">
              <Typography variant="body2" color="text.secondary">
                7-day LPC margin change
              </Typography>
            </Tooltip>
            <Typography variant="h4" sx={{ my: 0.5, fontVariantNumeric: 'tabular-nums' }}>
              {movement == null ? '—' : `${movement > 0 ? '+' : ''}${money(movement)}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {movement == null ? 'Comparable periods unavailable' : `7 days ending ${comparisonEndDate} vs previous 7`}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
